'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CameraLens } from '@/types/fleet';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '@/lib/api';
import {
  ackPhaseFromStatus,
  isConfirmedInFlightStatus,
  NO_ACK_DETAIL,
  type AckPhase,
} from '@/lib/command-ack';
import type { DroneAckPayload } from '@/types/coordination';

/**
 * Sends per-drone flight commands and tracks their acks.
 *
 * Modelled on useDockCommand, with one simplification: the drone plane
 * guarantees a command_id on both ends -- the executor generates it and returns
 * it from the POST, and both the MAVLink bridge (publish_ack) and the DJI
 * companion echo it back -- so acks correlate exactly. The dock hook's fallback
 * of matching on the command *name* is unnecessary here.
 *
 * A pending entry that is never acked expires rather than spinning forever,
 * because a drone that has lost its link will never answer — and it expires to a
 * visible failure, not to nothing. See ACK_PENDING_EXPIRE_MS.
 */

export type DroneCommandAction =
  | 'arm'
  | 'disarm'
  | 'takeoff'
  | 'land'
  /**
   * Tells an aircraft held by DJI landing protection that the surface below is
   * safe, so it finishes the descent (#200). Risk-REDUCING: the companion app
   * groups it with land/return_home/abort - no ARM, no preflight, never held.
   * The alternative to answering is hovering at ~1 m until the battery decides.
   */
  | 'confirm_landing'
  | 'return_home'
  | 'abort'
  | 'pause'
  | 'resume'
  | 'goto'
  | 'gimbal'
  | 'zoom'
  | 'lens';

/**
 * The camera verbs (issue #244). They are QUIET: no toast per send — a slew is
 * many sends and a toast per zoom tap is noise — and a POST failure surfaces as
 * a failed phase on the readout glyph instead of a toast. Everything else about
 * the ack lifecycle is identical to the flight verbs.
 *
 * Ack state is keyed per ACTION, so rapid repeats of the same camera verb
 * coalesce in the UI: a second `gimbal` send replaces the first's pending entry
 * and the first's ack no longer matches anything. Accepted v1 behaviour — the
 * operator watches the picture move, not nine ack lines.
 */
const CAMERA_ACTIONS: ReadonlySet<DroneCommandAction> = new Set(['gimbal', 'zoom', 'lens']);

export interface DroneCommandParams {
  altitude_m?: number;
  /** Required for `goto`. */
  latitude?: number;
  /** Required for `goto`. */
  longitude?: number;
  speed_ms?: number;
  /** `gimbal`: RELATIVE deltas in degrees. `center` wins over both deltas. */
  pitch_deg?: number;
  yaw_deg?: number;
  center?: boolean;
  /** `zoom`: exactly one of `ratio` | `step`. The COP only ever sends steps
   *  (or reset) — the step→target computation lives on the aircraft. */
  ratio?: number;
  step?: 'in' | 'out' | 'reset';
  /** `lens`: which camera feeds the video stream. Required for that action.
   *  A closed set on both sides — the RC app has SDK constants for exactly
   *  these three and reports anything else as unknown rather than rounding it. */
  lens?: CameraLens;
  /**
   * `land`: skip the dock landing sequence and descend where the aircraft
   * stands.
   *
   * A LAND for an aircraft over its home dock opens the bay and waits for the
   * dock to confirm it first (golden-flow steps 13-14), and that sequence can
   * refuse. This is the operator's way past every one of those refusals - an
   * aircraft that cannot be told to come down is worse than any bay the
   * sequence is protecting, so this must never be gated.
   */
  direct?: boolean;
}

interface DroneCommandAckState {
  commandId: string;
  phase: AckPhase;
  /**
   * The device's own status word, kept alongside the coarse phase.
   *
   * ackPhaseFromStatus collapses 'accepted' and 'executing' into a single
   * 'acked' phase, which is right for the dock UI but loses the distinction the
   * command bar needs to show a real lifecycle. Rather than widen the shared
   * AckPhase union (and every dock consumer of it), the raw word is retained
   * here and the bar reads it directly.
   */
  status: string | null;
  detail: string | null;
}

const ACK_TERMINAL_CLEAR_MS = 5_000;

/**
 * How long a command may sit un-terminal before the COP gives up on it.
 *
 * It MUST outlast the longest window any device plane will wait before sending
 * its own terminal ack, or the operator never sees the terminal ack at all. The
 * companion app's two longest are a takeoff (CommandTimeouts.TAKEOFF: 30 s for
 * the SDK's answer, then 30 s for an observed climb = 60 s worst case) and an
 * unarmed motor command (CommandTimeouts.ARM_HOLD_MS: 60 s). At the previous
 * 15 s this hook deleted the entry three quarters of a minute before the
 * `timed_out` ack that whole mechanism exists to produce could arrive, and the
 * subscriber's `if (!match) return` then dropped it — so a takeoff that never
 * happened rendered as a command that simply vanished.
 *
 * 75 s is those 60 s plus room for broker and ingest latency. Widen it if the
 * app's windows widen; the two numbers are a pair.
 *
 * One knowing exception: `start_mission` has no completion deadline on the
 * device, and its wayline-finished ack can arrive many minutes later, long after
 * this window has retired the readout — so a mission completion is not rendered
 * on the command bar. That is deliberate rather than overlooked. Holding an entry
 * open for the length of an arbitrary mission would leave a stale command on the
 * COP for the whole flight, and the mission's real progress belongs on the
 * mission surface, not on a button's ack line.
 */
const ACK_PENDING_EXPIRE_MS = 75_000;

/**
 * How long two identical ABORTs are treated as one press (#344).
 *
 * ABORT is the one verb that must stay re-issuable while its own command is
 * still in flight: `api.post` carries no timeout, so a stalled request on a
 * degraded link - the exact reason to abort - would otherwise be what stands
 * between the operator and the emergency stop. That exemption is deliberate and
 * pinned by test.
 *
 * But it is not a licence to publish a burst. On 2026-08-23 five STOPs went out
 * in 2 s from one gesture, and because ABORT disarms the landing auto-confirm
 * net by design, the burst repeatedly disarmed it and defeated the night's
 * hands-off landing test. A held key was already handled (`e.repeat`); a
 * hammered one was not.
 *
 * So: identical aborts inside this window collapse to one, and the next press
 * after it goes out normally. Short enough that a deliberate second abort is
 * never swallowed, long enough that no human gesture produces two.
 */
const ABORT_COLLAPSE_MS = 1_500;

export interface DroneCommandController {
  /** Command currently being POSTed (one at a time). */
  submitting: DroneCommandAction | null;
  /** Ack phase for a command, or null when nothing is in flight for it. */
  phaseOf: (action: DroneCommandAction) => AckPhase | null;
  /** Raw device status ('accepted' | 'executing' | ...), or null. */
  statusOf: (action: DroneCommandAction) => string | null;
  /** Detail text for a failed ack, or null. */
  failureOf: (action: DroneCommandAction) => string | null;
  /**
   * What the aircraft says it is DOING right now, or null.
   *
   * Only while the device's own word is `executing` — the one status that
   * carries a detail worth showing before the command is over. A wayline
   * reports its waypoints through it ("waypoint 3 of 5"), which is the
   * difference between a multi-leg route and a frozen one on the operator's
   * screen. Every other phase's detail is either a failure (`failureOf`) or
   * restates the step the bar is already drawing.
   */
  progressOf: (action: DroneCommandAction) => string | null;
  /** All commands whose ack came back failed, with detail. */
  failures: Array<[DroneCommandAction, string]>;
  /**
   * The most recent POST-level refusal for this action - the request never
   * reached the drone. Distinct from `failureOf`, which is the device's own
   * answer: a 409 from a landing/launch gate never dispatches anything, so
   * there is no ack to fail. Cleared by the action's next attempt (direct or
   * not) or a drone switch; nothing else retires it, so it survives long
   * enough for an operator to read it and decide the next step.
   */
  postFailureOf: (action: DroneCommandAction) => { code: string | null; message: string } | null;
  /**
   * Dispatch a command, resolving the id the executor minted for it.
   *
   * Null when nothing was dispatched — the hook is disabled, the aircraft
   * changed under the POST, or the POST failed (already reported). The id is
   * what lets a caller follow ONE command's acks: `dispatchManualFlyTo` needs it
   * because a `goto` sent after handing manual authority back may be HELD at the
   * RC for an ARM, and an operator who has just given up the sticks has to be
   * told that rather than reading "command sent".
   */
  send: (action: DroneCommandAction, params?: DroneCommandParams) => Promise<string | null>;
}

export function useDroneCommand({
  droneId,
  subscribeDroneAck,
  enabled = true,
}: {
  droneId: string;
  subscribeDroneAck: (cb: (ack: DroneAckPayload) => void) => () => void;
  /** False for viewers / uncommandable drones — send() becomes a no-op. */
  enabled?: boolean;
}): DroneCommandController {
  const [submitting, setSubmitting] = useState<DroneCommandAction | null>(null);
  const [ackState, setAckState] = useState<Record<string, DroneCommandAckState>>({});
  const [postError, setPostError] = useState<
    Partial<Record<DroneCommandAction, { code: string | null; message: string }>>
  >({});

  // The ref is the matching source of truth and is updated synchronously with
  // the state, not from an effect. Syncing it in a useEffect leaves it a render
  // behind, and an ack that lands in that window finds no entry to match and is
  // dropped — which the operator sees as a command that spins forever.
  const ackStateRef = useRef(ackState);
  const applyAckState = useCallback(
    (updater: (prev: Record<string, DroneCommandAckState>) => Record<string, DroneCommandAckState>) => {
      const next = updater(ackStateRef.current);
      if (next === ackStateRef.current) return;
      ackStateRef.current = next;
      setAckState(next);
    },
    [],
  );

  // Which drone the bar is currently pointed at, read inside async
  // continuations so a slow POST cannot land on a different aircraft.
  const droneIdRef = useRef(droneId);
  droneIdRef.current = droneId;

  const clearTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  /**
   * Commands whose POST is in the air RIGHT NOW, keyed `droneId:action` (with
   * `land:direct` as its own action - see the lock in `send`).
   *
   * A ref, not `submitting`, because `setSubmitting` is a React state update and
   * does not land until the next render: four clicks inside one frame all saw
   * `submitting === null` and all four POSTed. This is mutated synchronously in
   * `send`, so the second press of a burst sees the first.
   *
   * It covers only the POST window. The ack lifecycle after it is covered by the
   * pending entry in `ackStateRef` - together they are the whole in-flight
   * window, which is what #344 found `submitting` alone was not.
   */
  const postingRef = useRef<Set<string>>(new Set());
  /** When the last ABORT actually went out - see ABORT_COLLAPSE_MS. */
  const lastAbortAtRef = useRef(0);

  const scheduleAckClear = useCallback(
    (action: string, commandId: string, delayMs: number) => {
      const timers = clearTimersRef.current;
      const existing = timers.get(action);
      if (existing) clearTimeout(existing);
      timers.set(
        action,
        setTimeout(() => {
          timers.delete(action);
          applyAckState((prev) => {
            // A newer command for the same action supersedes this timer.
            if (prev[action]?.commandId !== commandId) return prev;
            return Object.fromEntries(Object.entries(prev).filter(([key]) => key !== action));
          });
        }, delayMs),
      );
    },
    [applyAckState],
  );

  /**
   * The pending/acked window elapsed with no TERMINAL ack. Every real ack
   * replaces this timer via scheduleAck*, so if it fires the command is
   * unconfirmed by construction.
   *
   * It expires to a VISIBLE failure rather than deleting the entry, the same way
   * useDockCommand does. A command that silently vanishes from the bar reads as
   * one that succeeded, which is precisely the fire-and-forget lie this PR
   * removes on the device side; removing it there and leaving it here would just
   * move where the operator is misled.
   */
  const scheduleAckExpire = useCallback(
    (action: string, commandId: string, delayMs: number) => {
      const timers = clearTimersRef.current;
      const existing = timers.get(action);
      if (existing) clearTimeout(existing);
      timers.set(
        action,
        setTimeout(() => {
          timers.delete(action);
          applyAckState((prev) => {
            if (prev[action]?.commandId !== commandId) return prev;
            return { ...prev, [action]: { ...prev[action], phase: 'failed', detail: NO_ACK_DETAIL } };
          });
          scheduleAckClear(action, commandId, ACK_TERMINAL_CLEAR_MS);
        }, delayMs),
      );
    },
    [applyAckState, scheduleAckClear],
  );

  useEffect(() => {
    const timers = clearTimersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  // Switching the selected drone must not leave the previous drone's acks on
  // screen — they would read as belonging to the newly selected aircraft.
  useEffect(() => {
    const timers = clearTimersRef.current;
    timers.forEach((t) => clearTimeout(t));
    timers.clear();
    applyAckState((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    // A POST-level refusal belongs to the drone it was refused for - carrying
    // it over would show a stale "Emergency RTH" for the newly selected one.
    setPostError((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    // The abort collapse is per aircraft. Holding it across a selection change
    // would swallow the first STOP aimed at the newly selected drone.
    lastAbortAtRef.current = 0;
  }, [droneId, applyAckState]);

  useEffect(() => {
    return subscribeDroneAck((ack) => {
      if (ack.drone_id !== droneId) return;
      if (typeof ack.command_id !== 'string') return;

      const match = Object.entries(ackStateRef.current).find(
        ([, st]) => st.commandId === ack.command_id,
      );
      if (!match) return;

      const [action, st] = match;
      const status = typeof ack.status === 'string' ? ack.status : null;
      const phase = ackPhaseFromStatus(status);
      const detail = typeof ack.detail === 'string' ? ack.detail : st.detail;

      applyAckState((prev) => ({ ...prev, [action]: { ...st, phase, status, detail } }));
      if (phase !== 'acked') {
        scheduleAckClear(action, st.commandId, ACK_TERMINAL_CLEAR_MS);
      } else if (!isConfirmedInFlightStatus(status)) {
        // Nothing has confirmed anything yet — `held` means the app never sent it
        // and `dispatched` means only that an SDK method was invoked. Silence here
        // really is a missing acknowledgement, so say so. Tested against the
        // CONFIRMED allowlist, not the unconfirmed one, so an unrecognised device
        // word lands on this side rather than being read as an acknowledgement.
        scheduleAckExpire(action, st.commandId, ACK_PENDING_EXPIRE_MS);
      } else {
        // `accepted` / `executing`: the aircraft DID acknowledge. land, return_home
        // and start_mission are given no completion deadline on the device on
        // purpose (CommandTracker.CommandTimeouts) — they park at accepted and
        // publish nothing further, because a landing takes minutes and the app
        // cannot know when it ended. Stamping "No acknowledgement received" here
        // would be a fabricated failure on a safety command that was acknowledged,
        // which is this PR's own defect with the sign flipped. Retire the stale
        // readout without claiming anything about the outcome.
        scheduleAckClear(action, st.commandId, ACK_PENDING_EXPIRE_MS);
      }
    });
  }, [subscribeDroneAck, droneId, scheduleAckClear, scheduleAckExpire, applyAckState]);

  const send = useCallback(
    async (action: DroneCommandAction, params?: DroneCommandParams): Promise<string | null> => {
      if (!enabled) return null;

      // #344: one gesture, one command. The same flight-command POST fired 4x in
      // 0.9 s on hardware because the only lock was the POST window, and the
      // window that matters is the whole ack lifecycle the hook already tracks.
      //
      // ABORT is exempt from the lifecycle lock (a stalled abort must stay
      // re-issuable) and gets a short time collapse instead, so a hammered STOP
      // cannot repeatedly disarm the landing net.
      // The lock is keyed by aircraft as well as action: the hook instance is
      // shared across selection, and drone A's held LAND POST (the bay wait
      // can run 45 s) must not silently swallow a LAND for newly selected B.
      //
      // ANY `direct: true` verb gets its own key and skips the pending-ack
      // lock. It is the escape hatch the whole dock sequence's safety rests on
      // - while the orchestrated verb is held server-side (or parked pending),
      // refusing `direct: true` under the same key made the raw path
      // impossible for the exact hang it exists for. Like ABORT, a duplicate
      // direct command is harmless; an unreachable one is not.
      //
      // This read `action === 'land'` until #403 gave TAKEOFF a bay sequence
      // too - and its 50 s hold is precisely when an operator reaches for the
      // direct key. RETURN DIRECTLY had the same gap: the button existed and
      // the lock could still swallow it.
      const isDirect = params?.direct === true;
      const lockKey = `${droneId}:${isDirect ? `${action}:direct` : action}`;
      if (action === 'abort') {
        const now = Date.now();
        if (now - lastAbortAtRef.current < ABORT_COLLAPSE_MS) return null;
        lastAbortAtRef.current = now;
      } else if (
        postingRef.current.has(lockKey)
        || (!isDirect && ackStateRef.current[action]?.phase === 'pending')
      ) {
        return null;
      }

      const issuedFor = droneId;
      postingRef.current.add(lockKey);
      setSubmitting(action);
      // A real attempt is going out - any refusal a previous attempt of this
      // action left standing is superseded, whether this one succeeds or not.
      setPostError((prev) => {
        if (!(action in prev)) return prev;
        const next = { ...prev };
        delete next[action];
        return next;
      });
      try {
        const res = await api.post<{ data: { command_id: string } }>(
          `/api/drones/${droneId}/flight-command`,
          { action, ...params },
        );
        // The operator may have selected another aircraft while this was in
        // flight. Recording the pending ack now would show drone A's command
        // under drone B's callsign, and drone A's real ack could never clear it.
        if (droneIdRef.current !== issuedFor) return null;
        applyAckState((prev) => ({
          ...prev,
          [action]: { commandId: res.data.command_id, phase: 'pending', status: null, detail: null },
        }));
        scheduleAckExpire(action, res.data.command_id, ACK_PENDING_EXPIRE_MS);
        if (!CAMERA_ACTIONS.has(action)) {
          toast.success(`Command sent: ${action.replace(/_/g, ' ').toUpperCase()}`);
        }
        return res.data.command_id;
      } catch (err) {
        if (CAMERA_ACTIONS.has(action)) {
          // Quiet verb, but never a silent failure: park a failed entry so the
          // camera surface's phase glyph shows the rejection. A toast here would
          // fire once per throttled send — a dead API during a slew is four
          // toasts a second. The synthetic local id keeps the clear timer's
          // supersede check working; no real ack can ever match it.
          if (droneIdRef.current !== issuedFor) return null;
          const localId = `local-post-failed-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          applyAckState((prev) => ({
            ...prev,
            [action]: {
              commandId: localId,
              phase: 'failed',
              status: null,
              detail: getApiErrorMessage(err, 'Failed to send command'),
            },
          }));
          scheduleAckClear(action, localId, ACK_TERMINAL_CLEAR_MS);
        } else {
          const message = getApiErrorMessage(err, 'Failed to send command');
          toast.error(message);
          // A machine-readable refusal reason, when the server sent one (e.g.
          // `landing_sequence_refused`) - duck-typed rather than an
          // `instanceof ApiRequestError` check, since all this needs is the
          // field the route actually put in the body.
          const code =
            err && typeof err === 'object' && 'code' in err
              && typeof (err as { code?: unknown }).code === 'string'
              ? (err as { code: string }).code
              : null;
          if (droneIdRef.current === issuedFor) {
            setPostError((prev) => ({ ...prev, [action]: { code, message } }));
          }
        }
        return null;
      } finally {
        postingRef.current.delete(lockKey);
        setSubmitting(null);
      }
    },
    [droneId, enabled, scheduleAckExpire, scheduleAckClear, applyAckState],
  );

  const phaseOf = useCallback(
    (action: DroneCommandAction) => ackState[action]?.phase ?? null,
    [ackState],
  );
  const statusOf = useCallback(
    (action: DroneCommandAction) => ackState[action]?.status ?? null,
    [ackState],
  );
  const failureOf = useCallback(
    (action: DroneCommandAction) => {
      const st = ackState[action];
      return st?.phase === 'failed' ? st.detail : null;
    },
    [ackState],
  );
  const progressOf = useCallback(
    (action: DroneCommandAction) => {
      const st = ackState[action];
      return st?.status === 'executing' ? st.detail : null;
    },
    [ackState],
  );
  const failures = Object.entries(ackState)
    .filter(([, st]) => st.phase === 'failed' && st.detail !== null)
    .map(([action, st]) => [action as DroneCommandAction, st.detail as string] as [DroneCommandAction, string]);
  const postFailureOf = useCallback(
    (action: DroneCommandAction) => postError[action] ?? null,
    [postError],
  );

  return { submitting, phaseOf, statusOf, failureOf, progressOf, failures, postFailureOf, send };
}
